import "./image-lightbox.css";
import { Dom } from "./dom";
import { Txt } from "./text";

export interface LightboxImage
{
    url: string;
    title?: string;
}

interface Registration
{
    trigger: HTMLElement;
    image: LightboxImage;
    group: string;
}

/**
 * jQuery-free replacement for Lightbox2.
 *
 * Two ways to use it:
 *   - ImageLightbox.register(element, image, group) binds a click on the element
 *     that opens a viewer scoped to all elements registered under the same group.
 *     The group members are gathered (in DOM order) at click time, so it works
 *     with dynamically rendered tables without any re-init.
 *   - ImageLightbox.open(images, startIndex) opens the viewer directly with an
 *     explicit list (e.g. a generated QR code).
 */
export class ImageLightbox
{
    private static registrations: Registration[] = [];

    private static overlay: HTMLDialogElement;
    private static imageElement: HTMLImageElement;
    private static spinner: HTMLElement;
    private static errorElement: HTMLElement;
    private static caption: HTMLElement;
    private static prevButton: HTMLElement;
    private static nextButton: HTMLElement;

    private static album: LightboxImage[] = [];
    private static index = 0;
    private static touchStartX: number | null = null;

    static register(trigger: HTMLElement, image: LightboxImage, group: string | number): void
    {
        const groupKey = String(group);
        this.registrations.push({ trigger, image, group: groupKey });
        trigger.style.cursor = "pointer";
        trigger.addEventListener("click", event =>
        {
            event.preventDefault();
            this.openGroup(groupKey, trigger);
        });
    }

    static open(images: LightboxImage[], startIndex = 0): void
    {
        if (!images || images.length === 0)
        {
            return;
        }
        this.album = images;
        this.index = Math.max(0, Math.min(startIndex, images.length - 1));
        this.ensureOverlay();
        this.render();
        this.show();
    }

    private static openGroup(groupKey: string, clicked: HTMLElement): void
    {
        // Drop registrations whose element is no longer in the document
        // (e.g. table cells that have been re-rendered).
        this.registrations = this.registrations.filter(r => document.body.contains(r.trigger));

        const members = this.registrations
            .filter(r => r.group === groupKey)
            .sort((a, b) =>
                a.trigger.compareDocumentPosition(b.trigger) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);

        const startIndex = Math.max(0, members.findIndex(r => r.trigger === clicked));
        this.open(members.map(r => r.image), startIndex);
    }

    private static ensureOverlay(): void
    {
        if (this.overlay)
        {
            return;
        }

        // A native <dialog> is used (like the app's Dialog) so the lightbox joins
        // the browser top layer and shows above any open modal dialog/form.
        this.overlay = Dom.create("dialog", "w-lightbox");
        this.overlay.addEventListener("click", event =>
        {
            if (event.target === this.overlay)
            {
                this.hide();
            }
        });
        this.overlay.addEventListener("close", this.onClose);

        const content = Dom.addDiv(this.overlay, "w-lightbox-content");

        this.imageElement = Dom.add("img", content, "w-lightbox-image");

        this.spinner = Dom.addDiv(content, "w-lightbox-spinner");

        this.errorElement = Dom.addDiv(content, "w-lightbox-error");
        this.errorElement.textContent = Txt.imageLoadFailed;

        // Arrows live on the overlay so they sit at the page edge, not the image edge.
        // SVG chevrons are geometrically centered (text glyphs are not).
        const chevron = (points: string) =>
            `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">` +
            `<polyline points="${points}" fill="none" stroke="currentColor" ` +
            `stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        this.prevButton = this.addNavButton(this.overlay, "w-lightbox-prev", chevron("15 5 8 12 15 19"), -1);
        this.nextButton = this.addNavButton(this.overlay, "w-lightbox-next", chevron("9 5 16 12 9 19"), 1);

        const closeButton = Dom.add("button", this.overlay, "w-lightbox-close");
        closeButton.setAttribute("type", "button");
        closeButton.setAttribute("aria-label", Txt.close);
        closeButton.innerHTML = "&times;";
        closeButton.addEventListener("click", () => this.hide());

        this.caption = Dom.addDiv(this.overlay, "w-lightbox-caption");

        document.body.appendChild(this.overlay);
    }

    private static addNavButton(parent: HTMLElement, className: string, glyph: string, step: number): HTMLElement
    {
        const button = Dom.add("button", parent, className);
        button.setAttribute("type", "button");
        button.innerHTML = glyph;
        button.addEventListener("click", event =>
        {
            event.stopPropagation();
            this.move(step);
        });
        return button;
    }

    private static move(step: number): void
    {
        const count = this.album.length;
        this.index = (this.index + step + count) % count;
        this.render();
    }

    private static render(): void
    {
        const current = this.album[this.index];
        const total = this.album.length;

        // Reset to the loading state for the new image.
        this.imageElement.classList.remove("is-loaded");
        this.imageElement.style.display = "none";
        this.errorElement.style.display = "none";
        this.spinner.style.display = "";

        this.imageElement.onload = () =>
        {
            this.spinner.style.display = "none";
            this.imageElement.style.display = "";
            this.imageElement.classList.add("is-loaded");
        };
        this.imageElement.onerror = () =>
        {
            this.spinner.style.display = "none";
            this.imageElement.style.display = "none";
            this.errorElement.style.display = "";
        };
        this.imageElement.src = current.url;
        this.imageElement.alt = current.title || "";

        const label = total > 1 ? this.index + 1 + " / " + total : "";
        const title = current.title || "";
        this.caption.textContent = title && label ? title + " — " + label : title || label;
        this.caption.style.display = this.caption.textContent ? "" : "none";

        const showNav = total > 1;
        this.prevButton.style.display = showNav ? "" : "none";
        this.nextButton.style.display = showNav ? "" : "none";
    }

    private static show(): void
    {
        if (this.overlay.open)
        {
            return;
        }
        this.overlay.showModal();
        document.body.classList.add("w-lightbox-open");
        document.addEventListener("keydown", this.onKeyDown);
        this.overlay.addEventListener("touchstart", this.onTouchStart, { passive: true });
        this.overlay.addEventListener("touchend", this.onTouchEnd, { passive: true });
        // Fade in once laid out.
        window.requestAnimationFrame(() => this.overlay.classList.add("is-open"));
    }

    private static hide(): void
    {
        // Triggers the "close" event, which runs onClose cleanup.
        this.overlay.close();
    }

    // Cleanup runs on close() and on native Escape-to-close.
    private static onClose = (): void =>
    {
        this.overlay.classList.remove("is-open");
        document.body.classList.remove("w-lightbox-open");
        document.removeEventListener("keydown", this.onKeyDown);
        this.overlay.removeEventListener("touchstart", this.onTouchStart);
        this.overlay.removeEventListener("touchend", this.onTouchEnd);
    };

    // Bound as arrow fields so add/removeEventListener reference the same function.
    // Escape is handled natively by the <dialog> element.
    private static onKeyDown = (event: KeyboardEvent): void =>
    {
        if (event.key === "ArrowRight" && this.album.length > 1)
        {
            this.move(1);
        }
        else if (event.key === "ArrowLeft" && this.album.length > 1)
        {
            this.move(-1);
        }
    };

    private static onTouchStart = (event: TouchEvent): void =>
    {
        this.touchStartX = event.changedTouches[0].clientX;
    };

    private static onTouchEnd = (event: TouchEvent): void =>
    {
        if (this.touchStartX === null || this.album.length < 2)
        {
            return;
        }
        const deltaX = event.changedTouches[0].clientX - this.touchStartX;
        this.touchStartX = null;
        if (Math.abs(deltaX) > 40)
        {
            this.move(deltaX < 0 ? 1 : -1);
        }
    };
}
